import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BarChart2, Lock, TrendingUp, DollarSign, Clock, Users, Sparkles } from 'lucide-react';
import { getStaffingAnalytics } from '../../lib/staffingApi';

/**
 * AnalyticsCard — Phase 11.
 *
 * Renders the Analytics slot inside the RecruiterDashboard "locked features"
 * strip. Two visual states driven by the mock plan:
 *
 *   plan="free"     → semi-locked preview: big total number + upsell CTA
 *                     that opens the UpgradeModal (feature="analytics").
 *   plan="business" → fully unlocked: 4-metric grid + horizontal breakdown
 *                     bar + "Last updated" timestamp.
 *
 * Refresh contract (spec):
 *   - Fetch on mount (with the current session id + plan).
 *   - Refetch every 30 seconds (polling).
 *   - Refetch immediately when the parent forwards a `refreshSignal` (e.g.
 *     WS session:complete). We compare prev vs next, and if it changed
 *     we schedule a 350 ms debounced refetch — that lag ensures the room
 *     write in the WS handler is durable before we re-aggregate.
 *
 * Error UX: silent retry — the polling interval keeps trying every 30 s.
 * The card shows a subtle inline "retrying…" note without blowing up the
 * dashboard, per the "graceful" requirement.
 */

const POLL_MS = 30_000;
const WS_DEBOUNCE_MS = 350;

// Small time-ago helper. Returns "just now" for <10s, "N min ago" up to an
// hour, and the ISO timestamp date after that.
function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

const dollarsFmt = (n) => `$${Number(n || 0).toLocaleString('en-US')}`;
const hoursFmt = (n) => `${Number(n || 0).toFixed(1)}h`;
const pctFmt = (n) => `${Number(n || 0).toFixed(1)}%`;

export default function AnalyticsCard({
  recruiterSessionId,
  plan,
  refreshSignal,       // any monotonic value that changes to trigger refetch
  onUpgradeClick,      // (feature: string) => void
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  // A stable ref to the debounce timer so multiple rapid refreshSignal
  // increments coalesce into a single refetch.
  const debounceRef = useRef(null);
  // A tick to force "N sec ago" to re-render without changing data.
  const [, setNowTick] = useState(0);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await getStaffingAnalytics(recruiterSessionId, plan);
      setData(res);
      setErrored(false);
    } catch (e) {
      // Keep last-known-good data on transient errors; only flip the flag.
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [recruiterSessionId, plan]);

  // Mount + plan-change → immediate fetch, then poll every 30s.
  useEffect(() => {
    setLoading(true);
    fetchAnalytics();
    const id = setInterval(fetchAnalytics, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAnalytics]);

  // WS session:complete → debounced refetch (parent bumps refreshSignal).
  useEffect(() => {
    if (refreshSignal === undefined || refreshSignal === null) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAnalytics();
    }, WS_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [refreshSignal, fetchAnalytics]);

  // Keep the "just now / N min ago" label fresh once a minute.
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // ------- Loading skeleton (first fetch only) -------
  if (loading && !data) {
    return (
      <div
        className="tc-analytics-card tc-analytics-card--loading"
        data-testid="analytics-card-loading"
      >
        <span className="tc-analytics-eyebrow">
          <BarChart2 className="tc-analytics-eyebrow-icon" />
          Analytics Dashboard
        </span>
        <div className="tc-analytics-skeleton" aria-hidden="true">
          <div className="tc-skeleton-line tc-skeleton-line--xl" />
          <div className="tc-skeleton-line tc-skeleton-line--sm" />
          <div className="tc-skeleton-line tc-skeleton-line--sm" />
        </div>
      </div>
    );
  }

  const isBusiness = data?.plan === 'business';

  // ------- FREE preview -------
  if (!isBusiness) {
    const total = data?.total_interviews ?? 0;
    return (
      <button
        type="button"
        className="tc-analytics-card tc-analytics-card--preview"
        onClick={() => onUpgradeClick?.('analytics')}
        data-testid="analytics-card-free"
        aria-label="Analytics preview — upgrade to Business to unlock"
      >
        <span className="tc-analytics-lock-badge" aria-hidden="true">
          <Lock className="w-3 h-3" />
        </span>
        <span className="tc-analytics-eyebrow">
          <BarChart2 className="tc-analytics-eyebrow-icon" />
          Analytics Dashboard
        </span>
        <div className="tc-analytics-preview-value">
          <span className="tc-analytics-preview-number" data-testid="analytics-total-free">
            {total}
          </span>
          <span className="tc-analytics-preview-label">
            {total === 1 ? 'Interview completed' : 'Interviews completed'}
          </span>
        </div>
        <p className="tc-analytics-preview-note">
          Unlock qualification rate, ROI metrics, and full breakdown with Business.
        </p>
        <span className="tc-analytics-preview-cta" data-testid="analytics-upgrade-cta">
          <Sparkles className="w-3.5 h-3.5" />
          See what&apos;s included →
        </span>
        {errored && (
          <span className="tc-analytics-error-inline" data-testid="analytics-error-free">
            Analytics temporarily unavailable, retrying…
          </span>
        )}
      </button>
    );
  }

  // ------- BUSINESS full -------
  const b = data.breakdown || { pre_qualified: 0, rejected: 0, review: 0 };
  const total = data.total_interviews ?? 0;
  const denom = Math.max(total, 1);
  const pctPre = (b.pre_qualified / denom) * 100;
  const pctRej = (b.rejected / denom) * 100;
  const pctRev = (b.review / denom) * 100;

  return (
    <div
      className="tc-analytics-card tc-analytics-card--business"
      data-testid="analytics-card-business"
      role="region"
      aria-label="Analytics dashboard (Business plan)"
    >
      <span className="tc-analytics-eyebrow">
        <BarChart2 className="tc-analytics-eyebrow-icon" />
        Analytics Dashboard
        <span className="tc-analytics-badge-live" data-testid="analytics-live-badge">
          LIVE
        </span>
      </span>

      <div className="tc-analytics-grid" data-testid="analytics-metrics-grid">
        <Metric
          icon={<Users className="w-4 h-4" />}
          testId="metric-total"
          label="Total Interviews"
          value={total}
        />
        <Metric
          icon={<TrendingUp className="w-4 h-4" />}
          testId="metric-qualification"
          label="Qualification Rate"
          value={pctFmt(data.qualification_percentage)}
        />
        <Metric
          icon={<DollarSign className="w-4 h-4" />}
          testId="metric-dollars"
          label="Dollars Saved"
          value={dollarsFmt(data.total_dollars_saved)}
        />
        <Metric
          icon={<Clock className="w-4 h-4" />}
          testId="metric-hours"
          label="Hours Saved"
          value={hoursFmt(data.total_hours_saved)}
        />
      </div>

      <div className="tc-analytics-breakdown" data-testid="analytics-breakdown-bar">
        <div className="tc-analytics-breakdown-bar" aria-hidden="true">
          {total === 0 ? (
            <div className="tc-analytics-breakdown-empty" />
          ) : (
            <>
              <span
                className="tc-analytics-seg tc-analytics-seg--pre"
                style={{ width: `${pctPre}%` }}
                title={`Pre-qualified: ${b.pre_qualified}`}
              />
              <span
                className="tc-analytics-seg tc-analytics-seg--rej"
                style={{ width: `${pctRej}%` }}
                title={`Rejected: ${b.rejected}`}
              />
              <span
                className="tc-analytics-seg tc-analytics-seg--rev"
                style={{ width: `${pctRev}%` }}
                title={`Review: ${b.review}`}
              />
            </>
          )}
        </div>
        <div className="tc-analytics-breakdown-legend">
          <LegendDot color="pre" label={`Pre-Qualified · ${b.pre_qualified}`} testId="legend-pre" />
          <LegendDot color="rej" label={`Rejected · ${b.rejected}`} testId="legend-rej" />
          <LegendDot color="rev" label={`Review · ${b.review}`} testId="legend-rev" />
        </div>
      </div>

      <div className="tc-analytics-footer">
        <span data-testid="analytics-computed-at">
          Last updated: {timeAgo(data.computed_at)}
        </span>
        {data.avg_duration_seconds > 0 && (
          <span data-testid="analytics-avg-duration">
            Avg duration: {data.avg_duration_seconds.toFixed(1)}s
          </span>
        )}
        {errored && (
          <span className="tc-analytics-error-inline" data-testid="analytics-error-business">
            Analytics temporarily unavailable, retrying…
          </span>
        )}
      </div>
    </div>
  );
}

function Metric({ icon, label, value, testId }) {
  return (
    <div className="tc-analytics-metric" data-testid={testId}>
      <span className="tc-analytics-metric-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="tc-analytics-metric-value">{value}</span>
      <span className="tc-analytics-metric-label">{label}</span>
    </div>
  );
}

function LegendDot({ color, label, testId }) {
  return (
    <span className="tc-analytics-legend-item" data-testid={testId}>
      <span className={`tc-analytics-legend-dot tc-analytics-legend-dot--${color}`} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}
